import { ConcreteFormStepProps } from './interfaces'
import { useTranslation } from 'react-i18next'
import { NextStep } from '../components/NextStep'
import * as yup from 'yup'
import { useStepForm } from './hooks'
import { Controller } from 'react-hook-form'
import { Checkbox, FormControlLabel, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import React from 'react'
import { checkVAT, countries } from 'jsvat'
import { getCompanyCountryCodeFor } from './utilities'

export const TaxNumber = ({ values, onSubmit, lead }: ConcreteFormStepProps) => {
    const companyCountryCode = getCompanyCountryCodeFor(lead)
    const vatPrefix = companyCountryCode === 'CH' ? 'CHE' : companyCountryCode

    const { t } = useTranslation()
    const schema = yup.object({
        noVatNumber: yup.boolean(),
        vatNumber: yup.string().when('noVatNumber', {
            is: false,
            then: (schema) => schema.test({
                name: 'valid-vat-number',
                test (value, ctx) {
                    return checkVAT(vatPrefix + value, countries).isValid
                }
            })
        }),
        taxNumber: yup.string().when('noVatNumber', {
            is: true,
            then: (schema) => schema.required().min(3)
        }),
    })
    const {
        control,
        handleSubmit,
        augmentedFieldProps,
        watch,
        setValue
    } = useStepForm('TaxNumber', schema, {
        ...values,
        vatNumber: values.vatNumber && values.vatNumber.substring(0, 2) === vatPrefix ? values.vatNumber.substring(2) : ''
    }, false)

    const isVatCountry = checkVAT(companyCountryCode, countries).isSupportedCountry
    const noVatNumber = watch('noVatNumber') ?? false

    React.useEffect(() => {
        if (!isVatCountry && noVatNumber === false) {
            setValue('noVatNumber', true)
        }
        if (isVatCountry && !watch('taxNumber')) {
            setValue('noVatNumber', false)
        }
    }, [isVatCountry])

    const showVat = isVatCountry && !noVatNumber

    const onSubmitWithDataTransform = (data: Record<string, any>) => {
        onSubmit({
            ...data,
            vatNumber: data.noVatNumber ? '' : checkVAT(vatPrefix + data.vatNumber, countries).value,
            taxNumber: data.noVatNumber ? data.taxNumber : '',
        })
    }

    return <>
        <Typography mb={4} variant="h5" component="h1">{t('TaxNumber.heading')}</Typography>

        <form onSubmit={handleSubmit(onSubmitWithDataTransform)}>
            <Stack maxWidth={500}>
                <Controller
                    name="vatNumber"
                    control={control}
                    defaultValue=""
                    render={({ field }) => <TextField
                        {...augmentedFieldProps(field)}
                        sx={showVat ? {} : { display: 'none' }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">{vatPrefix}</InputAdornment>,
                        }}
                    />}
                />
                <Controller
                    name="taxNumber"
                    control={control}
                    defaultValue=""
                    render={({ field }) => <TextField
                        {...augmentedFieldProps(field)}
                        sx={showVat ? { display: 'none' } : {}}
                    />}
                />
                <Controller
                    name="noVatNumber"
                    control={control}
                    render={({ field }) =>
                        <FormControlLabel
                            label={augmentedFieldProps(field).label}
                            sx={isVatCountry ? {} : { display: 'none' }}
                            control={
                                <Checkbox
                                    {...field}
                                    checked={noVatNumber}
                                />
                            }
                        />
                    }
                />
            </Stack>

            <NextStep/>
        </form>
    </>
}
